import React, { useCallback, useMemo, useState } from "react";
import { matchPath, useLocation, useRouteMatch } from "react-router-dom";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CreateNewFolderOutlined from "@mui/icons-material/CreateNewFolderOutlined";
import NoteAddOutlined from "@mui/icons-material/NoteAddOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PlaylistAddOutlined from "@mui/icons-material/PlaylistAddOutlined";
import PostAddOutlined from "@mui/icons-material/PostAddOutlined";
import TaskAltOutlined from "@mui/icons-material/TaskAltOutlined";
import { Typography } from "@mui/material";
import { WorkspaceDocumentIcon, WorkspaceFormIcon } from "assets/material-custom-icons";
import { DealCreate } from "components/modals";
import { Create } from "components/Prompts/Create";
import { useModals, useNavigate, useNavigationHooks, usePathParam, usePrompt, useUrlHooks } from "hooks";
import {
  useDealActions,
  useDealSelector,
  useDocumentsActions,
  useFolderSelect,
  usePickUserPermissions,
  useTaskManageActions,
  useUserRoleSelector,
  useUserSelector,
  useViewActions
} from "store";
import { UserPermissionType } from "store/types";

import { RoleName, Routes } from "../../../constants";
import { AddMenuActionId, getMenuConfigForPath } from "./add-menu-config";

const GeneralSectionHeader: React.FC<{ closeMenu: () => void }> = () => (
  <Typography
    sx={{
      fontSize: "12px",
      fontWeight: 600,
      color: "#496682",
      padding: "8px 16px 4px",
      textTransform: "uppercase",
      lineHeight: "16px"
    }}
  >
    General
  </Typography>
);

interface MenuOption {
  title: string;
  icon: React.ReactElement;
  onClick: () => void;
  hidden?: boolean;
  hasLine?: boolean;
}

type MenuEntry = MenuOption | React.FC<{ closeMenu: () => void }>;

const iconSx = { fontSize: 20, color: "#5d7782", fill: "#5d7782" };

export function useAddMenuOptionsHook() {
  const { pathname } = useLocation();
  const [isSelectDeal, setIsSelectDeal] = useState<((id: number, title: string) => void) | null>(null);

  const deal = useDealSelector();
  const { setCreateAction } = useTaskManageActions();
  const { setCreateManyMeasureData, setQuickViewAddNewLineAction } = useViewActions();
  const { createModal, filesUploadModal, selectDealFolderModal, openDataroomConnect } = useModals();
  const { createFolderAction, createFormAction } = useDealActions();
  const { createDocumentAction } = useDocumentsActions();
  const navigate = useNavigate();
  const { setParams } = useNavigationHooks();
  const openPrompt = usePrompt();

  const taskManager = usePickUserPermissions("task_manager");
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const myDeals = usePickUserPermissions("deals");
  const uploadFilesPermission = usePickUserPermissions("deals");
  const cloudIntegrations = usePickUserPermissions("cloud_integrations");
  const userRole = useUserRoleSelector();
  const { email } = useUserSelector();

  const dealId = usePathParam("deal");
  const folderId = usePathParam("folder");
  const { queryParams } = useUrlHooks();
  const sectionId = queryParams.section;
  const folder = useFolderSelect(Number(sectionId));

  const hideTaskManager =
    taskManager && (taskManager === UserPermissionType.NO_ACCESS || taskManager === UserPermissionType.VIEWER);
  const hideDealMetrics =
    dealMetrics && (dealMetrics === UserPermissionType.NO_ACCESS || dealMetrics === UserPermissionType.VIEWER);
  const hideMyDeals = myDeals && (myDeals === UserPermissionType.NO_ACCESS || myDeals === UserPermissionType.VIEWER);
  const hideUploadFiles =
    uploadFilesPermission &&
    (uploadFilesPermission === UserPermissionType.NO_ACCESS || uploadFilesPermission === UserPermissionType.VIEWER);
  const hidePortal = userRole?.name !== RoleName.Admin && cloudIntegrations !== UserPermissionType.ADMIN;
  const hideAddUser = userRole?.name !== RoleName.Admin;
  const isAltiusAdmin = email?.endsWith("@altius.capital") && userRole?.name === RoleName.Admin;

  
  const matchDealWorkspace = useRouteMatch({ path: Routes.dealWorkspace, exact: true });
  const matchDealWorkspaceAllFiles = useRouteMatch({ path: Routes.dealWorkspaceAllFiles, exact: true });
  const matchDealWorkspaceRecentlyAdded = useRouteMatch({ path: Routes.dealWorkspaceRecentlyAdded, exact: true });
  const matchDealFolder = useRouteMatch({ path: Routes.dealFolder, exact: true });
  const matchWorkspace = useRouteMatch({ path: Routes.workspace, exact: true });
  const matchWorkspaceFolder = useRouteMatch({ path: Routes.workspaceFolder, exact: true });
  const matchWorkspaceAllFiles = useRouteMatch({ path: Routes.workspaceAllFiles, exact: true });
  const matchWorkspaceRecentlyAdded = useRouteMatch({ path: Routes.workspaceRecentlyAdded, exact: true });

  const isFileRepositoryRoute = useMemo(
    () => !!matchPath(pathname, { path: Routes.fileRepository, exact: true }),
    [pathname]
  );

  const isWorkspaceRoute =
    !!matchDealWorkspace ||
    !!matchDealWorkspaceAllFiles ||
    !!matchDealWorkspaceRecentlyAdded ||
    !!matchDealFolder ||
    !!matchWorkspace ||
    !!matchWorkspaceFolder ||
    !!matchWorkspaceAllFiles ||
    !!matchWorkspaceRecentlyAdded;

  const handleAddFile = useCallback(() => {
    filesUploadModal({
      dealId,
      folderId,
      onSuccessCallback: async () => {
        if (isFileRepositoryRoute) {
          setParams({ search: { refetchFileRepository: true } });
          return;
        }
        if (isWorkspaceRoute) {
          setParams({ search: { refetchWorkspace: true } });
          return;
        }
      }
    });
  }, [filesUploadModal, dealId, folderId, setParams, isFileRepositoryRoute, isWorkspaceRoute]);

  const handleAddFolder = useCallback(() => {
    if (!dealId) return;
    createModal({
      action: async (name: string) => {
        await createFolderAction({
          dealId: Number(dealId),
          title: name,
          parent_id: folderId ? Number(folderId) : null
        });
      },
      title: "Add Folder",
      submitButtonTitle: "Add"
    });
  }, [createModal, createFolderAction, dealId, folderId]);

  const handleAddForm = useCallback(async () => {
    if (!dealId) return;
    let selectedSectionId: string | number | undefined = sectionId as string | undefined;
    if (folder?.kind !== "folder") {
      selectedSectionId = await new Promise<string | number | undefined>((resolve) => {
        selectDealFolderModal({
          isOpen: true,
          title: "Add a new form",
          preselectedDeal: { id: dealId, title: deal.title },
          onSubmit: ({ selectedFolder }) => {
            resolve(selectedFolder?.id);
          },
          onClose: () => {
            resolve(undefined);
          }
        });
      });
    }
    if (!selectedSectionId) return;

    const newFormTitle = await openPrompt({
      Component: Create,
      props: { title: "Add a new form" }
    });
    if (!newFormTitle) return;

    const data = await createFormAction({
      dealId: Number(dealId),
      sectionId: Number(selectedSectionId),
      title: newFormTitle
    });
    if (data.deal_id && data.section_id) {
      navigate({
        route: "dealForm",
        params: { deal_id: data.deal_id, form_id: data.id },
        search: { section: data.section_id }
      });
    }
  }, [sectionId, folder?.kind, dealId, deal.title, selectDealFolderModal, openPrompt, createFormAction, navigate]);

  const handleAddDocument = useCallback(async () => {
    if (!dealId) return;
    let selectedSectionId: string | number | undefined = sectionId as string | undefined;
    if (folder?.kind !== "folder") {
      selectedSectionId = await new Promise<string | number | undefined>((resolve) => {
        selectDealFolderModal({
          isOpen: true,
          title: "Add a new document",
          preselectedDeal: { id: dealId, title: deal.title },
          onSubmit: ({ selectedFolder }) => {
            resolve(selectedFolder?.id);
          },
          onClose: () => {
            resolve(undefined);
          }
        });
      });
    }
    if (!selectedSectionId) return;

    const newDocumentTitle = await openPrompt({
      Component: Create,
      props: { title: "Add a new document" }
    });
    if (!newDocumentTitle) return;

    const data = await createDocumentAction({
      deal_id: Number(dealId),
      section_id: Number(selectedSectionId),
      title: newDocumentTitle
    });
    if (data.deal_id && data.section_id) {
      navigate({
        route: "dealDocument",
        params: { deal_id: data.deal_id, document_id: data.id },
        search: { section: data.section_id }
      });
    }
  }, [sectionId, folder?.kind, dealId, deal.title, selectDealFolderModal, openPrompt, createDocumentAction, navigate]);

  const handleAddDeal = useCallback(async () => {
    const data = await openPrompt({ Component: DealCreate, props: {}, disableBackdropClose: true });
    if (!data) return;
    navigate({ route: "deal", params: { deal_id: data.id } });
  }, [openPrompt, navigate]);

  const handleAddValue = useCallback(() => {
    if (deal.id) {
      setCreateManyMeasureData({ dealId: deal.id, dealTitle: deal.title });
      return;
    }
    setIsSelectDeal(() => async (id: number, title: string) => {
      setCreateManyMeasureData({ dealId: id, dealTitle: title });
    });
  }, [setCreateManyMeasureData, deal]);

  const handleAddTask = useCallback(() => {
    setCreateAction({ isOpen: true, disableRedirectOnSubmit: true });
  }, [setCreateAction]);

  const handleAddPortal = useCallback(() => {
    openDataroomConnect();
  }, [openDataroomConnect]);

  const handleAddUser = useCallback(() => {
    setParams({ search: { userManagement: "create" } });
  }, [setParams]);

  const handleAddLineItem = useCallback(() => {
    setQuickViewAddNewLineAction(true);
  }, [setQuickViewAddNewLineAction]);

  const handleUploadToCloud = useCallback(() => {
    filesUploadModal({ isCloudMirror: true });
  }, [filesUploadModal]);

  const allHidden =
    !!hideMyDeals && !!hideDealMetrics && !!hideTaskManager && !!hideUploadFiles && !!hidePortal && !isAltiusAdmin;

  const options = useMemo(() => {
    const actions: Record<AddMenuActionId, MenuOption> = {
      addFile: {
        title: "Add File",
        icon: <NoteAddOutlined sx={iconSx} />,
        onClick: handleAddFile,
        hidden: !!hideUploadFiles
      },
      addFolder: {
        title: "Add Folder",
        icon: <CreateNewFolderOutlined sx={iconSx} />,
        onClick: handleAddFolder,
        hidden: !dealId || !!hideUploadFiles
      },
      addForm: {
        title: "Add Form",
        icon: <WorkspaceFormIcon sx={iconSx} />,
        onClick: handleAddForm,
        hidden: !dealId || !!hideUploadFiles
      },
      addDocument: {
        title: "Add Document",
        icon: <WorkspaceDocumentIcon sx={iconSx} />,
        onClick: handleAddDocument,
        hidden: !dealId || !!hideUploadFiles
      },
      addDeal: {
        title: "Add Deal",
        icon: <AddCircleOutline sx={iconSx} />,
        onClick: handleAddDeal,
        hidden: !!hideMyDeals
      },
      addValue: {
        title: "Add Value",
        icon: <PostAddOutlined sx={iconSx} />,
        onClick: handleAddValue,
        hidden: !!hideDealMetrics
      },
      addTask: {
        title: "Add Task",
        icon: <TaskAltOutlined sx={iconSx} />,
        onClick: handleAddTask,
        hidden: !!hideTaskManager
      },
      addPortal: {
        title: "Add Portal",
        icon: <CloudOutlinedIcon sx={iconSx} />,
        onClick: handleAddPortal,
        hidden: hidePortal
      },
      addUser: {
        title: "Add User",
        icon: <PersonAddOutlinedIcon sx={iconSx} />,
        onClick: handleAddUser,
        hidden: hideAddUser
      },
      addLineItem: {
        title: "Add Line Item",
        icon: <PlaylistAddOutlined sx={iconSx} />,
        onClick: handleAddLineItem,
        hidden: !!hideDealMetrics
      },
      uploadToCloud: {
        title: "Upload to Cloud",
        icon: <CloudUploadOutlinedIcon sx={iconSx} />,
        onClick: handleUploadToCloud,
        hidden: !isAltiusAdmin
      }
    };

    const config = getMenuConfigForPath(pathname);

    const contextualItems = config.contextual.map((id) => actions[id]);
    const generalItems = config.general.map((id) => actions[id]);

    const hasVisibleContextual = contextualItems.some((item) => !item.hidden);

    if (!hasVisibleContextual) {
      return generalItems as MenuEntry[];
    }

    // Set hasLine on the last visible contextual item
    const result: MenuEntry[] = contextualItems.map((item, index) => {
      const isLastVisible = !item.hidden && contextualItems.slice(index + 1).every((next) => next.hidden);
      if (isLastVisible) {
        return { ...item, hasLine: true };
      }
      return item;
    });

    result.push(GeneralSectionHeader);
    result.push(...generalItems);

    return result;
  }, [
    pathname,
    dealId,
    hideUploadFiles,
    hideMyDeals,
    hideDealMetrics,
    hideTaskManager,
    hidePortal,
    hideAddUser,
    isAltiusAdmin,
    handleAddFile,
    handleAddFolder,
    handleAddForm,
    handleAddDocument,
    handleAddDeal,
    handleAddValue,
    handleAddTask,
    handleAddPortal,
    handleAddUser,
    handleAddLineItem,
    handleUploadToCloud
  ]);

  return { options, isSelectDeal, setIsSelectDeal, allHidden };
}
